import React, { useState } from 'react'
import { FiDownload } from 'react-icons/fi'
import dayjs from 'dayjs'

const BookingManagementPage: React.FC = () => {
  const bookings = [
    {
      bookingId: 'C-001',
      patientName: 'John Doe',
      therapistName: 'Dr. Emily Ross',
      serviceType: 'Physiotherapy',
      bookingDate: '2025-09-25',
      appointmentDateTime: '2025-09-28T10:00',
      duration: '60 min',
      status: 'Confirmed',
      paymentStatus: 'Paid',
      amount: 50,
    },
    {
      bookingId: 'C-002',
      patientName: 'Sarah Smith',
      therapistName: 'Dr. Alan Lee',
      serviceType: 'Speech Therapy',
      bookingDate: '2025-09-26',
      appointmentDateTime: '2025-09-29T14:00',
      duration: '45 min',
      status: 'Confirmed',
      paymentStatus: 'Pending',
      amount: 40,
    },
    {
      bookingId: 'C-004',
      patientName: 'Anna White',
      therapistName: 'Dr. David Wong',
      serviceType: 'Occupational Ther',
      bookingDate: '2025-09-27',
      appointmentDateTime: '2025-10-01T11:00',
      duration: '60 min',
      status: 'Cancelled',
      paymentStatus: 'Refunded',
      amount: 55,
    },
  ]

  // 🔹 Filter States
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [paymentFilter, setPaymentFilter] = useState('')
  const [bookingDate, setBookingDate] = useState('')
  const [sessionDate, setSessionDate] = useState('')

  // 🔹 Apply filters
  const filteredBookings = bookings.filter(b => {
    const searchMatch =
      b.patientName.toLowerCase().includes(search.toLowerCase()) ||
      b.therapistName.toLowerCase().includes(search.toLowerCase()) ||
      b.bookingId.toLowerCase().includes(search.toLowerCase())

    const statusMatch = statusFilter ? b.status === statusFilter : true
    const paymentMatch = paymentFilter
      ? b.paymentStatus === paymentFilter
      : true
    const bookingDateMatch = bookingDate ? b.bookingDate === bookingDate : true
    const sessionDateMatch = sessionDate
      ? b.appointmentDateTime.slice(0, 10) === sessionDate
      : true

    return (
      searchMatch &&
      statusMatch &&
      paymentMatch &&
      bookingDateMatch &&
      sessionDateMatch
    )
  })

  // Counts
  const totalBookings = filteredBookings.length
  const scheduledBookings = filteredBookings.filter(
    b => b.status.toLowerCase() === 'schedule'
  ).length
  const cancelledBookings = filteredBookings.filter(
    b => b.status.toLowerCase() === 'cancelled'
  ).length

  return (
    <div className="p-6 mx-auto max-w-7xl">
      <div className="flex flex-col items-start justify-between gap-4 mb-2 sm:flex-row sm:items-center">
        <h1 className="text-2xl font-semibold text-[#026730]">Contract</h1>

        {/* Export CSV Button */}
        <div className="ml-auto">
          <button className="flex items-center h-12 gap-2 px-4 py-2 text-gray-700 transition-colors border border-gray-300 rounded-lg hover:bg-gray-50">
            <FiDownload className="w-4 h-4" />
            Export CSV
          </button>
        </div>
      </div>

      {/* Search + Filters Row */}
      <div className="flex flex-wrap gap-4 mb-6">
        <input
          type="text"
          placeholder="Contract id"
          className="flex-1 min-w-[180px] h-12 px-4 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
        />
        <input
          type="text"
          placeholder="Patient Name"
          className="flex-1 min-w-[150px] h-12 px-4 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
        />
        <input
          type="text"
          placeholder="Provider Name"
          className="flex-1 min-w-[150px] h-12 px-4 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
        />
        <select className="flex-1 min-w-[150px] h-12 px-4 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500">
          <option value="">Booking Status</option>
          <option value="Active">Confirmed</option>
          <option value="InActive">Cancelled</option>
        </select>
        {/* Date Range */}
        <input
          type="date"
          className="flex-1 min-w-[150px] h-12 px-4 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
        />
        <input
          type="date"
          className="flex-1 min-w-[150px] h-12 px-4 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
        />
      </div>

      {/* Cards Section */}
      <div className="grid grid-cols-1 gap-6 mb-6 sm:grid-cols-3">
        <div className="flex flex-col items-center justify-center p-4 bg-white rounded-lg shadow h-35">
          <h2 className="text-xl font-bold text-gray-900">{totalBookings}</h2>
          <p className="text-sm text-gray-500">Total Contract</p>
        </div>
        <div className="flex flex-col items-center justify-center p-4 bg-white rounded-lg shadow">
          <h2 className="text-xl font-bold text-yellow-600">5</h2>
          <p className="text-sm text-gray-500">Confirmed</p>
        </div>
        <div className="flex flex-col items-center justify-center p-4 bg-white rounded-lg shadow">
          <h2 className="text-xl font-bold text-red-600">
            {cancelledBookings}
          </h2>
          <p className="text-sm text-gray-500">Cancelled</p>
        </div>
      </div>

      {/* Table Section */}
      <div className="overflow-hidden bg-white rounded-lg shadow">
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead className="bg-gray-50">
              <tr className="text-sm text-left text-gray-600 uppercase">
                <th className="px-6 py-3 font-medium">Contract ID</th>
                <th className="px-6 py-3 font-medium">Patient Name</th>
                <th className="px-6 py-3 font-medium">Provider Name</th>
                <th className="px-6 py-3 font-medium">Service Type</th>
                <th className="px-6 py-3 font-medium">Booking Date</th>
                <th className="px-6 py-3 font-medium">Session Date & Time</th>
                <th className="px-6 py-3 font-medium">Duration</th>
                <th className="px-6 py-3 font-medium">Booking Status</th>
                {/* <th className="px-6 py-3 font-medium">Payment Status</th> */}
                <th className="px-6 py-3 font-medium">Amount</th>
                <th className="px-6 py-3 font-medium text-center">Contract</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredBookings.length > 0 ? (
                filteredBookings.map(booking => (
                  <tr key={booking.bookingId} className="hover:bg-gray-50">
                    <td className="px-6 py-4">{booking.bookingId}</td>
                    <td className="px-6 py-4">{booking.patientName}</td>
                    <td className="px-6 py-4">{booking.therapistName}</td>
                    <td className="px-6 py-4">{booking.serviceType}</td>
                    <td className="px-6 py-4">
                      {booking.bookingDate
                        ? dayjs(booking.bookingDate).isValid()
                          ? dayjs(booking.bookingDate).format('MM/DD/YYYY')
                          : '--'
                        : '--'}
                    </td>
                    <td className="px-6 py-4">
                      {new Date(booking.appointmentDateTime).toLocaleString()}
                    </td>
                    <td className="px-6 py-4">{booking.duration}</td>
                    <td className="px-6 py-4">
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium border ${
                          booking.status === 'Confirmed'
                            ? 'border-green-500 text-green-600'
                            : booking.status === 'Completed'
                              ? 'border-blue-500 text-blue-600'
                              : 'border-red-500 text-red-600' // default for everything else
                        }`}
                      >
                        {booking.status}
                      </span>
                    </td>

                    {/* <td className="px-6 py-4">
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium border ${
                          booking.paymentStatus === 'Paid'
                            ? 'border-green-500 text-green-600'
                            : booking.paymentStatus === 'Pending'
                              ? 'border-yellow-500 text-yellow-600'
                              : 'border-red-500 text-red-600'
                        }`}
                      >
                        {booking.paymentStatus}
                      </span>
                    </td> */}
                    <td className="px-6 py-4">${booking.amount}.00</td>
                    <td className="px-6 py-4">
                      <a
                        // href={txn.invoiceLink}
                        className="flex items-center gap-1 text-blue-600 hover:underline"
                      >
                        <FiDownload className="w-4 h-4" />
                        Download
                      </a>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan={10}
                    className="px-6 py-4 text-center text-gray-500"
                  >
                    No bookings found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

export default BookingManagementPage
